import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, switchMap } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { AppSettingsService } from '../../app-settings/app-settings.service';

@Injectable()
export class HspaAPIClientInterceptor implements HttpInterceptor {
    constructor(
        private appSettingsService: AppSettingsService,
        private oauthService: OAuthService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.oauthService.getAccessToken();

        if (token && request.url.match(/api\//)) {
            return from(this.encryptToken(token)).pipe(
                switchMap((encryptedToken) => {
                    const clonedRequest = request.clone({
                        setHeaders: {
                            Authorization: encryptedToken,
                            Type: 'Web',
                            Platform: this.getOS()
                        }
                    });
                    return next.handle(clonedRequest);
                })
            );
        }

        return next.handle(request);
    }

    private async encryptToken(token: string): Promise<string> {
        const keyBase64 = this.appSettingsService.getTokenBase64Key;
        const keyBytes = Uint8Array.from(atob(keyBase64), c => c.charCodeAt(0));
        const key = await crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['encrypt']);

        const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
        const encoded = new TextEncoder().encode(token);
        const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

        const encryptedBytes = new Uint8Array(encrypted);
        const combined = new Uint8Array(iv.length + encryptedBytes.length);
        combined.set(iv);
        combined.set(encryptedBytes, iv.length);

        return btoa(String.fromCharCode(...combined));
    }

    private getOS(): string {
        const { userAgent, platform } = window.navigator;
        const macos = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
        const windows = ['Win32', 'Win64', 'Windows', 'WinCE'];
        const ios = ['iPhone', 'iPad', 'iPod'];

        if (macos.includes(platform)) return 'Mac OS';
        if (ios.includes(platform)) return 'iOS';
        if (windows.includes(platform)) return 'Windows';
        if (/Android/.test(userAgent)) return 'Android';
        if (/Linux/.test(platform)) return 'Linux';

        return 'Unknown';
    }
}
